import { useCallback, useMemo } from "react";

import { secureJsonParse } from "_helpers/utils/jsonParser";
import { useFeatureFlags } from "hooks/useFeatureFlags";

interface HiddenEdifyContentConfig {
  packageId: string;
  hiddenChapterIds?: string[];
}

interface HiddenEdifyContentInterface {
  shouldHideEdifyModule: (packageId: string) => boolean;
  shouldHideEdifyModuleSubject: (packageId: string, chapterId: string) => boolean;
}

// Defines some util functions to check whether a specific Edify package shall be hidden
// or if only part of it should based on the contents of the "hiddenEdifyContent" variable in ConfigCat
export const useHiddenEdifyContent = (): HiddenEdifyContentInterface => {
  const { hiddenEdifyContent } = useFeatureFlags();

  const hiddenEdifyContentConfig = useMemo(
    () => secureJsonParse<HiddenEdifyContentConfig[]>(hiddenEdifyContent as string) || [],
    [hiddenEdifyContent],
  );

  const getHiddenEdifyModuleConfig = useCallback(
    (packageId: string): HiddenEdifyContentConfig | null =>
      hiddenEdifyContentConfig.find((moduleConfig) => moduleConfig.packageId === packageId) || null,
    [hiddenEdifyContentConfig],
  );

  // We shall hide module if it exists in hiddenEdifyContent and no specific chapters are specified
  const shouldHideEdifyModule = (packageId: string) => {
    const hiddenModuleConfig = getHiddenEdifyModuleConfig(packageId);
    return !!hiddenModuleConfig && !hiddenModuleConfig.hiddenChapterIds?.length;
  };

  // Subjects must be hidden is the chapter id exists in the "hiddenChapterIds" array for the specified package
  const shouldHideEdifyModuleSubject = (packageId: string, chapterId: string): boolean => {
    const hiddenModuleConfig = getHiddenEdifyModuleConfig(packageId);
    return (
      !!hiddenModuleConfig &&
      !!hiddenModuleConfig.hiddenChapterIds &&
      hiddenModuleConfig.hiddenChapterIds.includes(chapterId)
    );
  };

  return {
    shouldHideEdifyModule,
    shouldHideEdifyModuleSubject,
  };
};
